import {
  FoodItemTag,
  FoodTagBasic,
  FoodTagCategory,
  MenuItemFoodTagOverride,
  MenuItemFoodTagOverrideAction,
  MenuItemFoodTagOverrideCreate,
} from "client/jspPlatformExperiment";
import { activeFoodItemTagIds } from "utils/food/foodTagDisplayUtils";

/**
 * Menu-level food tag overrides: records the server applies on top of the
 * food's own tags when a menu is read with tag aggregation. One record is one
 * action over a list of tags (`tag_ids`), optionally scoped to a category.
 * The only control offered for now is the "Molle only" preset: one
 * `keep_only` record on the Molle texture tag in the texture category. The
 * server holds the intent and re-evaluates it against the food's current tags
 * on every read.
 */

/** Molle texture tag (see `byTextureOrder` in utils/food/foodTagDisplayUtils). */
export const MOLLE_TAG_ID = 22;

export type TagOverrides = MenuItemFoodTagOverride[] | null | undefined;

/** Write shape for the save body: drops the read-only `food_tags` / `created_at`. */
export const toOverrideCreate = (
  o: MenuItemFoodTagOverride,
): MenuItemFoodTagOverrideCreate => ({
  tag_ids: o.tag_ids ?? [],
  override_action: o.override_action ?? MenuItemFoodTagOverrideAction.REMOVE,
  ...(o.tag_category ? { tag_category: o.tag_category } : {}),
});

/**
 * The food's own Molle texture tag, when it carries one. Tags the user removed
 * from the food do not count. Undefined means the preset does not apply.
 */
export const findMolleTag = (
  foodTags: FoodItemTag[] | null | undefined,
): FoodItemTag | undefined => {
  const active = activeFoodItemTagIds(foodTags);
  return (foodTags ?? []).find(
    (t) =>
      t.id === MOLLE_TAG_ID &&
      active.has(t.id) &&
      t.tag_categories.includes(FoodTagCategory.FOOD_TEXTURE),
  );
};

export const isMolleOnlyOverride = (o: MenuItemFoodTagOverride): boolean =>
  o.override_action === MenuItemFoodTagOverrideAction.KEEP_ONLY &&
  o.tag_category === FoodTagCategory.FOOD_TEXTURE &&
  (o.tag_ids ?? []).length === 1 &&
  o.tag_ids?.[0] === MOLLE_TAG_ID;

export const buildMolleOnlyOverride = (
  molleTag: FoodTagBasic,
): MenuItemFoodTagOverride => ({
  override_action: MenuItemFoodTagOverrideAction.KEEP_ONLY,
  tag_category: FoodTagCategory.FOOD_TEXTURE,
  tag_ids: [molleTag.id],
  food_tags: [
    {
      id: molleTag.id,
      tag_name: molleTag.tag_name,
      tag_categories: molleTag.tag_categories,
      tag_description: molleTag.tag_description,
    },
  ],
});

/**
 * The item is either "Molle only" or has no override: on replaces the whole
 * list with the preset record, off clears it (including any legacy records).
 */
export const setMolleOnly = ({
  molleTag,
  enabled,
}: {
  molleTag: FoodTagBasic;
  enabled: boolean;
}): MenuItemFoodTagOverride[] =>
  enabled ? [buildMolleOnlyOverride(molleTag)] : [];

/** Indicator label; null when there is nothing to show. */
export const describeOverrides = (overrides: TagOverrides): string | null => {
  const rows = overrides ?? [];
  if (rows.length === 0) {
    return null;
  }
  const molleOnly = rows.some(isMolleOnlyOverride);
  const otherCount = rows
    .filter((o) => !isMolleOnlyOverride(o))
    .reduce((sum, o) => sum + (o.tag_ids ?? []).length, 0);
  if (molleOnly) {
    return otherCount > 0 ? `Molle only +${otherCount} hidden` : "Molle only";
  }
  return `${otherCount} tag${otherCount === 1 ? "" : "s"} hidden`;
};
